import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button'
import { PostChallengeNotificationPrompt } from '@/components/PostChallengeNotificationPrompt'
import type { Challenge } from '@/types/challenge'
import type { Challenge } from '@/types/challenge'


interface SuccessStepProps {
  selectedChallenge: Challenge | null
  customTitle: string
  createdChallenge: Challenge
  onComplete: () => void
  onCreateAnother: () => void
}

export function SuccessStep({ 
  selectedChallenge, 
  customTitle,
  onComplete,
  onCreateAnother 
}: SuccessStepProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [participantCount, setParticipantCount] = useState<number>(0)
  const [showConfetti, setShowConfetti] = useState(false)
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(true)

  const challengeTitle = selectedChallenge?.name || customTitle

  useEffect(() => {
    // Animate the participant count using data from the challenge (if catalog) or default to 1
    const finalCount = selectedChallenge?.participantsCount || 1
    setShowConfetti(true)
    
    // Animate counting up to the final number
    let current = 0
    const increment = Math.ceil(finalCount / 30) // Complete animation in ~30 steps
    const timer = setInterval(() => {
      current += increment
      if (current >= finalCount) {
        current = finalCount
        clearInterval(timer)
      }
      setParticipantCount(current)
    }, 50)

    return () => clearInterval(timer)
  }, [selectedChallenge?.participantsCount])

  const handleViewChallenge = () => {
    // Navigate to home page where users can see all their challenges
    navigate('/')
    onComplete()
  }

  const handleCreateAnother = () => {
    // Use the provided reset function instead of navigation
    onCreateAnother()
  }

  const handleNotificationPromptDismiss = () => {
    setShowNotificationPrompt(false)
  }

  return (
    <div className="space-y-xl text-center">
      {/* Success Animation */}
      <div className="relative">
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none">
            {/* Simple confetti effect using emoji */}
            <div className="absolute top-0 left-1/4 animate-bounce text-2xl">🎉</div>
            <div className="absolute top-4 right-1/4 animate-bounce text-2xl" style={{ animationDelay: '0.2s' }}>✨</div>
            <div className="absolute top-8 left-1/2 animate-bounce text-2xl" style={{ animationDelay: '0.4s' }}>🎊</div>
            <div className="absolute top-2 left-1/6 animate-bounce text-2xl" style={{ animationDelay: '0.6s' }}>⭐</div>
            <div className="absolute top-6 right-1/6 animate-bounce text-2xl" style={{ animationDelay: '0.8s' }}>🚀</div>
          </div>
        )}
        
        <div className="text-6xl mb-lg">🎯</div>
        <h1 className="text-3xl font-bold text-text mb-md">
          {t('challenges.challengeCreated_title')}
        </h1>
        <p className="text-lg text-text-muted mb-lg max-w-md mx-auto">
          {t('challenges.challengeIsActive', { name: challengeTitle })}
        </p>
      </div>

      {/* Notification Prompt */}
      {showNotificationPrompt && (
        <PostChallengeNotificationPrompt
          challengeTitle={challengeTitle}
          onDismiss={handleNotificationPromptDismiss}
        />
      )}

      {/* Participant Count */}
      <div className="bg-primary/10 border border-primary rounded-xl p-xl">
        <div className="text-center">
          <div className="text-4xl font-bold text-primary mb-sm">
            {participantCount.toLocaleString()}
          </div>
          <p className="text-lg text-text">
            {t('challenges.workingOnChallenge', { count: participantCount })}
          </p>
          <p className="text-sm text-text-muted mt-sm">
            {t('challenges.joinedCommunity')}
          </p>
        </div>
      </div>

      {/* Motivational Message */}
      <div className="bg-surface border border-border rounded-xl p-lg">
        <h3 className="font-semibold text-text mb-sm">{t('challenges.whatsNext')}</h3>
        <ul className="text-left text-sm text-text-muted space-y-xs">
          <li>• {t('challenges.receiveTimelyReminders')}</li>
          <li>• {t('challenges.stayConsistent')}</li>
          <li>• {t('challenges.shareProgress')}</li>
          <li>• {t('challenges.adjustFrequency')}</li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-md justify-center pt-lg">
        <Button
          onClick={handleViewChallenge}
          size="lg"
          className="sm:min-w-[200px]"
        >
          {t('challenges.viewMyChallenge')}
        </Button>
        <Button
          onClick={handleCreateAnother}
          variant="secondary"
          size="lg"
          className="sm:min-w-[200px]"
        >
          {t('challenges.createAnotherChallenge')}
        </Button>
      </div>

      {/* Fun Stats */}
      <div className="text-xs text-text-faint">
        💡 {t('challenges.trackingFactoid')}
      </div>
    </div>
  )
} 